import { useEffect, useMemo, useState } from 'react';
import {
  isWorkerOffline,
  lastReportedAt,
  pickByCapacity,
  subscriptionUsableIn,
  usageCapacity,
  withSubscriptionUsage,
  workerVersionState,
  type Agent,
  type AgentIntelligence,
  type AgentKind,
  type AgentProvider,
} from '@shared/domain.ts';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago, cn } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { Page, Row } from './Page.tsx';
import { CapacityBadge, QueuePanel, SubscriptionsPanel } from '@/components/Capacity.tsx';
import {
  Button,
  Dialog,
  Empty,
  Field,
  Input,
  Select,
  Textarea,
  Tip,
  useToast,
} from '@/components/ui.tsx';

/**
 * Routines.
 *
 * A routine is a URL that accepts a POST, and that is genuinely all Rigg knows
 * about it — a Claude Code routine can be started and never managed again,
 * which is the whole reason the callback API exists. Everything else on this
 * screen is about the two credentials that flow in opposite directions:
 *
 *   dispatch token — ours to present when we POST *to* the routine
 *   agent token    — the routine's to present when it calls *us*, and what
 *                    lets it open tasks of its own
 */
export function AgentsScreen() {
  const { data, project, isAdmin } = useRigg();
  const now = useNow();
  const { notify } = useToast();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [tokensFor, setTokensFor] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [pausingId, setPausingId] = useState<string | null>(null);

  const agents = useMemo(
    () =>
      Object.values(data.agents).sort(
        (a, b) => a.dispatch_seq - b.dispatch_seq || a.name.localeCompare(b.name),
      ),
    [data.agents],
  );

  // A paused routine sits out the rotation, so it is never who is up next. A
  // smart routine sits out the *ordinary* rotation the same way, without being
  // paused — it has a rotation of its own, among the other smart routines, for
  // tasks tagged smart. So "next up" is really two answers, one per level —
  // each the same pick the server makes: most room on the subscription first,
  // then whoever was least recently sent to.
  const nextUpByLevel = useMemo(() => {
    const eligible = agents
      .filter((a) => !a.paused && a.kind !== 'design')
      .map((a) => withSubscriptionUsage(a, data.subscriptions));
    return {
      normal: pickByCapacity(eligible.filter((a) => a.intelligence === 'normal'), now),
      smart: pickByCapacity(eligible.filter((a) => a.intelligence === 'smart'), now),
    };
  }, [agents, data.subscriptions, now]);
  const nextUpIds = useMemo(
    () => new Set([nextUpByLevel.normal?.id, nextUpByLevel.smart?.id].filter((id): id is string => Boolean(id))),
    [nextUpByLevel],
  );
  const routines = useMemo(() => agents.filter((a) => a.kind !== 'design'), [agents]);
  const projectTasks = useMemo(
    () => Object.values(data.tasks).filter((t) => !project || t.project_id === project.id),
    [data.tasks, project],
  );

  // Nothing to reshuffle with fewer than two, or if every seq is already the
  // default — the button would just do nothing.
  const rotationDirty = agents.some((a) => a.dispatch_seq !== 0);

  const resetRotation = async () => {
    if (!project) return;
    setResetting(true);
    try {
      await api.resetRotation(project.id);
      notify('Rotation reset', { body: 'Next unpinned task can land on any routine again.' });
    } catch (error) {
      notify('Could not reset the rotation', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setResetting(false);
    }
  };

  const togglePause = async (agent: Agent) => {
    setPausingId(agent.id);
    try {
      await api.updateAgent(agent.id, { paused: !agent.paused });
      notify(agent.paused ? `${agent.name} resumed` : `${agent.name} paused`, {
        body: agent.paused
          ? 'Back in the rotation for new tasks.'
          : 'Out of the rotation until resumed — still reachable by name.',
      });
    } catch (error) {
      notify(agent.paused ? 'Could not resume' : 'Could not pause', {
        body: (error as Error).message,
        tone: 'bad',
      });
    } finally {
      setPausingId(null);
    }
  };

  return (
    <Page
      title="Agents"
      blurb={
        <>
          Each one is a Claude Code routine: a name, an endpoint that accepts a POST, and an
          optional bearer token. Rigg checks each Claude subscription&rsquo;s limits hourly, and the
          next task goes to the routine whose subscription has the most room; among equals, whoever
          was least recently sent to, so adding or removing one does not reshuffle the rotation.
          Pausing a routine takes it out of that pick
          without deregistering it — a task can still be sent to it by name. A routine set to smart
          keeps its own rotation, for tasks tagged smart, and otherwise sits out the ordinary one —
          no pausing required.{' '}
          <a href="/protocol" onClick={(e) => { e.preventDefault(); navigate('/protocol'); }}>
            See what they are told →
          </a>
        </>
      }
      actions={
        isAdmin ? (
          <>
            {agents.length > 1 ? (
              <Tip content="Puts every routine back to the front of the queue, in the order they were registered.">
                <Button
                  disabled={resetting || !rotationDirty}
                  onClick={() => void resetRotation()}
                >
                  Reset rotation
                </Button>
              </Tip>
            ) : null}
            <Button tone="primary" onClick={() => setEditing('new')}>
              Register a routine
            </Button>
          </>
        ) : null
      }
      wide
    >
      {project && routines.length > 0 ? (
        <>
          <SubscriptionsPanel
            projectId={project.id}
            subscriptions={Object.values(data.subscriptions).filter((s) => subscriptionUsableIn(s, project.id))}
            agents={routines}
            nextUp={nextUpIds}
            isAdmin={isAdmin}
            now={now}
          />
          <QueuePanel projectId={project.id} tasks={projectTasks} now={now} />
        </>
      ) : null}

      {agents.length === 0 ? (
        <Empty
          title="No routines yet"
          hint="Register one and the composer will start accepting work."
        />
      ) : (
        <div>
          {agents.map((agent) => (
            <div key={agent.id} className={agent.paused ? 'opacity-60' : undefined}>
              <Row
                title={
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{agent.name}</span>
                    {agent.paused ? (
                      <Tip content="Out of the rotation — no unpinned task will land here until resumed.">
                        <span className="shrink-0 rounded-full bg-pill px-2 py-px text-[11px] font-semibold text-t7">
                          paused
                        </span>
                      </Tip>
                    ) : agent.id === nextUpByLevel[agent.intelligence]?.id ? (
                      <Tip
                        content={
                          agent.intelligence === 'smart'
                            ? 'Next in the smart rotation — the next task tagged smart goes here.'
                            : 'Next in the rotation — the next unpinned task goes here.'
                        }
                      >
                        <span className="shrink-0 rounded-full bg-accent/15 px-2 py-px text-[11px] font-semibold text-accent">
                          next up
                        </span>
                      </Tip>
                    ) : null}
                    {agent.intelligence === 'smart' ? (
                      <Tip content="Only auto-picked for tasks tagged smart — sits out the ordinary rotation.">
                        <span className="shrink-0 rounded-full bg-pill px-2 py-px text-[11px] font-semibold text-t7">
                          smart
                        </span>
                      </Tip>
                    ) : null}
                    {agent.subscription_id && data.subscriptions[agent.subscription_id] ? (
                      <Tip content="The Claude subscription it runs on. Routines on one share its limits.">
                        <span className="max-w-[30%] min-w-0 shrink truncate rounded-full bg-pill px-2 py-px text-[11px] text-t7">
                          {data.subscriptions[agent.subscription_id]!.name}
                        </span>
                      </Tip>
                    ) : null}
                    {agent.repo_id ? (
                      // The name is the primary identifier here; this badge is
                      // secondary and gives way to it first on a narrow phone.
                      <span className="max-w-[40%] min-w-0 truncate rounded-full bg-pill px-2 py-px font-mono text-[11px] text-t7">
                        {data.repos[agent.repo_id]?.name ?? 'repo'}
                      </span>
                    ) : null}
                  </span>
                }
                subtitle={
                  agent.kind === 'design' ? (
                    <span className="text-[11.5px]">asks for design passes · no inbound URL</span>
                  ) : (
                    <span className="font-mono text-[11.5px]">{agent.url}</span>
                  )
                }
                meta={
                  agent.kind === 'design' ? (
                    <span className="flex items-center gap-2.5">
                      <WorkerVersion agent={agent} />
                      <WorkerLight agent={agent} now={now} />
                    </span>
                  ) : (
                    <span className="flex items-center gap-2.5">
                      {lastReportedAt(withSubscriptionUsage(agent, data.subscriptions).usage) ? (
                        <CapacityBadge
                          capacity={usageCapacity(withSubscriptionUsage(agent, data.subscriptions), now)}
                          now={now}
                        />
                      ) : null}
                      <span
                        className={cn(
                          agent.last_result === 'error' ? 'text-warnx' : 'text-mute',
                        )}
                      >
                        {agent.last_dispatch_at
                          ? `${agent.dispatch_count} sent · last ${ago(agent.last_dispatch_at)}`
                          : 'never used'}
                      </span>
                    </span>
                  )
                }
                actions={
                  isAdmin ? (
                    <>
                      <Button
                        size="sm"
                        disabled={pausingId === agent.id}
                        onClick={() => void togglePause(agent)}
                      >
                        {agent.paused ? 'Resume' : 'Pause'}
                      </Button>
                      <Button size="sm" onClick={() => setTokensFor(agent.id)}>
                        Tokens
                      </Button>
                      <Button size="sm" onClick={() => setEditing(agent.id)}>
                        Edit
                      </Button>
                    </>
                  ) : null
                }
              >
                {agent.last_result === 'error' && agent.last_error ? (
                  <div className="rounded-lg border border-warnx/25 bg-warnx/6 px-3 py-2 font-mono text-[11.5px] leading-relaxed text-warnx">
                    {agent.last_error}
                  </div>
                ) : null}
                {agent.notes ? (
                  <div className="text-[12.5px] leading-relaxed text-soft">{agent.notes}</div>
                ) : null}
              </Row>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <AgentDialog
          agentId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onError={(message) => notify('Could not save', { body: message, tone: 'bad' })}
        />
      ) : null}

      {tokensFor ? <TokenDialog agentId={tokensFor} onClose={() => setTokensFor(null)} /> : null}

      {!project ? null : null}
    </Page>
  );
}

/**
 * One entry per provider Rigg knows the protocol for. The label is what the
 * operator picks; everything the endpoint actually requires — version headers,
 * beta flags — lives in `providerHeaders` and is never typed by hand.
 */
const PROVIDERS: { value: AgentProvider; label: string; hint: string }[] = [
  {
    value: 'anthropic',
    label: 'Anthropic — Claude Code routine',
    hint: 'A routine trigger URL from claude.ai/code/routines, with that routine’s own token. Rigg sends the headers Anthropic requires.',
  },
];

function AgentDialog({
  agentId,
  onClose,
  onError,
}: {
  agentId: string | null;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { data, project, projects } = useRigg();
  const existing = agentId ? data.agents[agentId] : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [kind, setKind] = useState<AgentKind>(existing?.kind ?? 'routine');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [repoId, setRepoId] = useState(existing?.repo_id ?? 'any');
  const [token, setToken] = useState('');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [provider, setProvider] = useState<AgentProvider>(existing?.provider ?? 'anthropic');
  const [intelligence, setIntelligence] = useState<AgentIntelligence>(
    existing?.intelligence ?? 'normal',
  );
  const [subscriptionId, setSubscriptionId] = useState(existing?.subscription_id ?? 'none');
  const subscriptions = Object.values(data.subscriptions)
    .filter((sub) => subscriptionUsableIn(sub, existing?.project_id ?? project?.id ?? ''))
    .sort((a, b) => a.name.localeCompare(b.name));
  const [busy, setBusy] = useState(false);

  const repos = Object.values(data.repos);

  const save = async () => {
    if (!project) return;
    setBusy(true);
    try {
      // A design worker has no address and presents no dispatch token: Rigg
      // never calls it, it calls Rigg. Sending either would be refused, and
      // clearing them here is what lets an existing routine be converted.
      const isPull = kind === 'design';
      const payload = {
        name: name.trim(),
        kind,
        url: isPull ? null : url.trim(),
        repo_id: repoId === 'any' ? null : repoId,
        notes: notes.trim() || null,
        provider,
        intelligence,
        subscription_id: isPull || subscriptionId === 'none' ? null : subscriptionId,
        ...(isPull || !token.trim() ? {} : { token: token.trim() }),
      };
      if (existing) await api.updateAgent(existing.id, payload);
      else await api.addAgent({ project_id: project.id, ...payload });
      onClose();
    } catch (error) {
      onError((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={existing ? `Edit ${existing.name}` : 'Register a routine'}
      description={
        kind === 'design'
          ? 'A design worker asks Rigg for work rather than being sent it, so it needs no URL and no inbound network — which is what lets it run on a machine on somebody\u2019s desk. Give it an agent token under Tokens.'
          : 'Rigg POSTs the brief to this URL. The repository, base branch and working directory are configured on the routine itself, so a task never carries them.'
      }
      footer={
        <>
          {existing ? (
            <Button
              tone="danger"
              className="mr-auto"
              disabled={busy}
              onClick={async () => {
                try {
                  await api.removeAgent(existing.id);
                  onClose();
                } catch (error) {
                  onError((error as Error).message);
                }
              }}
            >
              Remove
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            tone="primary"
            disabled={busy || !name.trim() || (kind !== 'design' && !url.trim())}
            onClick={() => void save()}
          >
            {existing ? 'Save' : 'Register'}
          </Button>
        </>
      }
    >
      <Field label="Name" hint="Only has to be unique inside this project.">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="routine-alpha" autoFocus />
      </Field>
      <Field
        label="Kind"
        hint={
          kind === 'design'
            ? 'Polls for design passes and reports back. Never enters the rotation and is never sent an ordinary task.'
            : 'Rigg pushes work to it. This is the ordinary kind.'
        }
      >
        <Select
          value={kind}
          onChange={(value) => setKind(value as AgentKind)}
          options={[
            { value: 'routine', label: 'Routine — Rigg sends it work' },
            { value: 'design', label: 'Design worker — it asks for work' },
          ]}
        />
      </Field>
      {kind === 'design' ? null : (
      <Field
        label="Intelligence"
        hint={
          intelligence === 'smart'
            ? 'Sits out the ordinary rotation — only auto-picked for a task explicitly tagged smart. Still reachable by name at any time.'
            : 'The default. Gets ordinary, untagged work from the rotation.'
        }
      >
        <Select
          value={intelligence}
          onChange={(value) => setIntelligence(value as AgentIntelligence)}
          options={[
            { value: 'normal', label: 'Normal' },
            { value: 'smart', label: 'Smart' },
          ]}
        />
      </Field>
      )}
      {kind === 'design' ? null : (
      <Field label="Provider" hint={PROVIDERS.find((p) => p.value === provider)?.hint}>
        <Select
          value={provider}
          onChange={(value) => setProvider(value as AgentProvider)}
          options={PROVIDERS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </Field>
      )}
      {kind === 'design' ? null : (
      <Field
        label="Subscription"
        hint={
          subscriptions.length === 0
            ? 'No subscriptions yet — add one per Claude account under Subscriptions on this screen, then pick it here.'
            : 'The Claude account this routine runs on. Its limits decide whether the next task comes here.'
        }
      >
        <Select
          value={subscriptionId}
          onChange={setSubscriptionId}
          options={[
            { value: 'none', label: 'None' },
            ...subscriptions.map((sub) => ({
              value: sub.id,
              label:
                sub.project_id === (existing?.project_id ?? project?.id)
                  ? sub.name
                  : `${sub.name} (shared from ${projects.find((p) => p.id === sub.project_id)?.name ?? 'another project'})`,
            })),
          ]}
        />
      </Field>
      )}
      {kind === 'design' ? null : (
      <Field
        label="Endpoint"
        hint="The routine's fire URL, from its API trigger — https://api.anthropic.com/v1/claude_code/routines/…/fire. The brief is sent as the run's context; the repository and branch come from the routine itself."
      >
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://api.anthropic.com/v1/claude_code/routines/trig_…/fire"
        />
      </Field>
      )}
      {repos.length > 0 ? (
        <Field label="Repository" hint="A routine wired to one repo can only be sent tasks for that repo.">
          <Select
            value={repoId}
            onChange={setRepoId}
            options={[
              { value: 'any', label: 'Any repository in this project' },
              ...repos.map((r) => ({ value: r.id, label: `${r.owner}/${r.name}` })),
            ]}
          />
        </Field>
      ) : null}
      {kind === 'design' ? null : (
      <Field
        label="Token"
        hint={
          existing
            ? 'Stored where neither the browser nor Realtime can reach it. Leave blank to keep the current one.'
            : 'The routine’s own token from its API trigger — starts `sk-ant-oat01-`. Not an Anthropic API key: this one can only fire this routine.'
        }
      >
        <Input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          type="password"
          placeholder={existing ? 'unchanged' : ''}
          autoComplete="off"
        />
      </Field>
      )}
      <Field label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-16" />
      </Field>
    </Dialog>
  );
}

/**
 * Agent tokens.
 *
 * The plaintext exists in exactly one HTTP response and is never recoverable —
 * only its SHA-256 is stored. So the moment it is minted is the moment it has
 * to be handed over, together with the instructions that make it useful.
 */
function TokenDialog({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const { data } = useRigg();
  const { notify } = useToast();
  const agent = data.agents[agentId];

  const [tokens, setTokens] = useState<
    { id: string; hint: string; label: string | null; created_at: string; last_used_at: string | null; revoked_at: string | null }[]
  >([]);
  const [minted, setMinted] = useState<{ token: string; instructions: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .agentTokens(agentId)
      .then((result) => setTokens(result.tokens))
      .catch(() => setTokens([]));
  };

  useEffect(load, [agentId]);

  const copy = async (text: string, what: string) => {
    await navigator.clipboard.writeText(text);
    notify(`${what} copied`);
  };

  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => !open && onClose()}
      title={`Tokens for ${agent?.name ?? 'this routine'}`}
      description="An agent token lets a routine report on its own tasks and open new ones — including work nobody asked for, with a category of its choosing."
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {minted ? (
        <div className="flex flex-col gap-3 rounded-xl border border-accent/30 bg-accent/6 p-4">
          <div className="text-[13px] font-[650] text-accent">
            Copy this now — it is not shown again.
          </div>
          <code className="rounded-lg border border-inputbd bg-input px-3 py-2 font-mono text-[12px] break-all text-t1">
            {minted.token}
          </code>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" tone="primary" onClick={() => void copy(minted.token, 'Token')}>
              Copy token
            </Button>
            <Button size="sm" onClick={() => void copy(minted.instructions, 'Instructions')}>
              Copy the instructions
            </Button>
          </div>
          <details className="text-[12.5px] text-body">
            <summary className="cursor-pointer font-semibold text-t2">
              What to give the routine
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-lg border border-line bg-brief p-3 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-t6">
              {minted.instructions}
            </pre>
          </details>
        </div>
      ) : null}

      <div>
        {tokens.length === 0 ? (
          <p className="text-[13px] text-mute">No agent tokens yet.</p>
        ) : (
          tokens.map((token) => (
            <Row
              key={token.id}
              title={<span className="font-mono text-[13px]">{token.hint}</span>}
              subtitle={token.label ?? undefined}
              meta={
                token.revoked_at
                  ? 'revoked'
                  : token.last_used_at
                    ? `last used ${ago(token.last_used_at)}`
                    : 'never used'
              }
              actions={
                token.revoked_at ? null : (
                  <Button
                    size="sm"
                    tone="danger"
                    onClick={async () => {
                      await api.revokeToken(token.id).catch(() => undefined);
                      load();
                    }}
                  >
                    Revoke
                  </Button>
                )
              }
            />
          ))
        )}
      </div>

      <Button
        tone="primary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const result = await api.mintAgentToken(agentId);
            setMinted({ token: result.token, instructions: result.instructions });
            load();
          } catch (error) {
            notify('Could not mint a token', { body: (error as Error).message, tone: 'bad' });
          } finally {
            setBusy(false);
          }
        }}
      >
        Mint a new token
      </Button>
    </Dialog>
  );
}

/**
 * Is the design worker running the current code?
 *
 * Silent when it is, which is nearly always — a badge that is present on a
 * healthy worker is one nobody reads by the second week. It appears when the
 * answer is worth acting on: a worker that has fallen behind and will fix
 * itself within the minute, or one that cannot because somebody has left
 * changes in the checkout on that machine.
 */
function WorkerVersion({ agent }: { agent: Agent }) {
  const state = workerVersionState(agent);
  if (state === 'current' || state === 'unknown') return null;

  const behind = agent.worker_behind ?? 0;
  const commits = `${behind} commit${behind === 1 ? '' : 's'} behind`;

  return (
    <Tip
      content={
        state === 'modified'
          ? `Running ${agent.worker_version ?? 'an unknown version'} with uncommitted changes, so it is not updating itself${behind ? ` — and it is ${commits}` : ''}. Somebody is working on that machine.`
          : `Running ${agent.worker_version ?? 'an unknown version'}, ${commits}. It pulls and restarts itself between passes.`
      }
    >
      <span className={cn('text-[11.5px]', state === 'modified' ? 'text-warnx' : 'text-mute')}>
        {state === 'modified' ? 'modified checkout' : commits}
      </span>
    </Tip>
  );
}

/**
 * Is the design worker running?
 *
 * The one thing you cannot ask a push routine and can ask this one. It polls on
 * a timer, so the age of its last call is a direct read of whether the machine
 * is on — and a red light here is the difference between "the queue is quiet"
 * and "the Mac mini rebooted last night and nobody noticed".
 */
function WorkerLight({ agent, now }: { agent: Agent; now: number }) {
  const offline = isWorkerOffline(agent, now);
  return (
    <Tip
      content={
        agent.last_seen_at
          ? `Last asked for work ${ago(agent.last_seen_at)}`
          : 'This worker has never asked for work — it has not been started'
      }
    >
      <span className={cn('flex items-center gap-1.5', offline ? 'text-warnx' : 'text-good')}>
        <span
          aria-hidden
          className={cn('size-2 rounded-full', offline ? 'bg-warnx' : 'bg-good')}
        />
        {offline ? 'down' : 'up'}
      </span>
    </Tip>
  );
}

